import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { getClient, deleteClient } from '../services/clients';
import ContactInfoTab from '../components/client/ContactInfoTab';
import GenerateInvoiceTab from '../components/client/GenerateInvoiceTab';
import InvoiceHistoryTab from '../components/client/InvoiceHistoryTab';
import PaymentInfoTab from '../components/client/PaymentInfoTab';
import NotesTab from '../components/client/NotesTab';
import ScheduleTaskTab from '../components/client/ScheduleTaskTab';
import TaskHistoryTab from '../components/client/TaskHistoryTab';
import OrderFormTab from '../components/client/OrderFormTab';
import UnpaidInvoicesBanner from '../components/UnpaidInvoicesBanner';
import OpenEstimatesBanner from '../components/OpenEstimatesBanner';
import ImportantNotesPopup from '../components/ImportantNotesPopup';
import './ClientProfile.css';

export default function ClientProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || searchParams.get('tab') || 'contact');
  const [showImportantNotes, setShowImportantNotes] = useState(false);
  const scrollToInvoiceIdFromUrl = searchParams.get('invoiceId') || location.state?.invoiceId;

  const loadClient = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getClient(id);
      setClient(data);
    } catch (error) {
      console.error('Error loading client:', error);
      alert('Failed to load client: ' + error.message);
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadClient();
  }, [loadClient]);

  // Check for important notes when client loads
  useEffect(() => {
    const checkImportantNotes = async () => {
      if (client) {
        try {
          const { getImportantClientNotes } = await import('../services/notes');
          const importantNotes = await getImportantClientNotes(client.id);
          if (importantNotes && importantNotes.length > 0) {
            setShowImportantNotes(true);
          }
        } catch (error) {
          console.error('Error checking important notes:', error);
        }
      }
    };
    checkImportantNotes();
  }, [client]);

  // Set active tab from location state or URL search params
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    } else if (searchParams.get('tab')) {
      setActiveTab(searchParams.get('tab'));
    }
  }, [location.state, searchParams]);

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this client? This action cannot be undone.')) {
      try {
        await deleteClient(id);
        navigate('/clients');
      } catch (error) {
        alert('Failed to delete client: ' + error.message);
      }
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="client-profile">
        <div className="error-message">Client not found</div>
      </div>
    );
  }

  const tabs = [
    { id: 'contact', label: 'Contact Info' },
    { id: 'invoice', label: 'Generate Estimate' },
    { id: 'history', label: 'Estimate & Invoice History' },
    { id: 'payment', label: 'Payment Info' },
    { id: 'notes', label: 'Notes' },
    { id: 'schedule', label: 'Schedule Task' },
    { id: 'task-history', label: 'Task History' },
    { id: 'order-form', label: 'Order Form' },
  ];

  return (
    <div className="client-profile">
      <div className="profile-header">
        <button className="btn-back" onClick={() => navigate('/clients')}>
          ← Back to Clients
        </button>
        <div className="header-actions">
          <button className="btn-delete-profile" onClick={handleDelete}>
            Delete Client
          </button>
        </div>
      </div>

      <div className="profile-content">
        <div className="client-name-header">
          <h1>{client.name}</h1>
        </div>

        <UnpaidInvoicesBanner clientId={client.id} />
        <OpenEstimatesBanner clientId={client.id} />

        <div className="tabs-container">
          <div className="tabs-nav">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="tab-content">
            {activeTab === 'contact' && (
              <ContactInfoTab client={client} onUpdate={loadClient} />
            )}
            {activeTab === 'invoice' && (
              <GenerateInvoiceTab client={client} />
            )}
            {activeTab === 'history' && (
              <InvoiceHistoryTab clientId={client.id} client={client} scrollToInvoiceId={scrollToInvoiceIdFromUrl} />
            )}
            {activeTab === 'payment' && (
              <PaymentInfoTab clientId={client.id} />
            )}
            {activeTab === 'notes' && (
              <NotesTab clientId={client.id} client={client} />
            )}
            {activeTab === 'schedule' && (
              <ScheduleTaskTab clientId={client.id} client={client} />
            )}
            {activeTab === 'task-history' && (
              <TaskHistoryTab clientId={client.id} />
            )}
            {activeTab === 'order-form' && (
              <OrderFormTab client={client} notificationId={location.state?.notificationId} />
            )}
          </div>
        </div>
      </div>

      {/* Important Notes Popup */}
      {showImportantNotes && (
        <ImportantNotesPopup
          clientId={client.id}
          clientName={client.name}
          onClose={() => setShowImportantNotes(false)}
        />
      )}
    </div>
  );
}
